/**
 * Finance / P&L month-detail page (A7Y-2054).
 *
 * Full breakdown for a single 'YYYY-MM' month: spend by category + vendor and
 * revenue attributed per org. Data source: /admin/finance/pnl/month/:month
 * served by a7y-api. Reached by clicking a row in the P&L Monthly tab.
 */
import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { AlertTriangle, ArrowLeft, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { MonthDetailSections } from "./components/MonthDetailSections";

const MONTH_RE = /^\d{4}-\d{2}$/;

/** 'YYYY-MM' → e.g. 'July 2026'. Falls back to the raw key if unparseable. */
function formatMonthLabel(month: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) return month;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  if (monthIndex < 0 || monthIndex > 11) return month;
  const date = new Date(Date.UTC(year, monthIndex, 1));
  return date.toLocaleString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function BackLink() {
  return (
    <Link
      to="/admin/finance/pnl"
      className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400"
    >
      <ArrowLeft className="h-4 w-4" />
      Back to P&amp;L
    </Link>
  );
}

export function PnlMonthDetailPage() {
  const { month } = useParams<{ month: string }>();
  const isValidMonth = month != null && MONTH_RE.test(month);

  const monthQ = useQuery({
    queryKey: ["finance-pnl", "month", month],
    queryFn: () => api.getPnlMonth(month as string),
    enabled: isValidMonth,
  });

  if (!isValidMonth) {
    return (
      <div className="px-4 py-0">
        <PageHeader title="P & L — invalid month">
          <BackLink />
        </PageHeader>
        <div className="flex items-center gap-2 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-700 dark:text-red-300">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Invalid month "{month}". Expected format YYYY-MM.
        </div>
      </div>
    );
  }

  const detail = monthQ.data?.data;

  return (
    <div className="px-4 py-0">
      <PageHeader
        title={formatMonthLabel(month)}
        subtitle="Spend by category & vendor and revenue attributed per org."
      >
        <div className="flex items-center gap-3 text-xs text-zinc-500">
          {monthQ.isFetching && (
            <span className="inline-flex items-center gap-1">
              <RefreshCw className="h-3 w-3 animate-spin" />
              refreshing…
            </span>
          )}
          <BackLink />
        </div>
      </PageHeader>

      {monthQ.isError && (
        <div className="mb-6 flex items-center gap-2 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-700 dark:text-red-300">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Month detail failed to load. Check the Worker logs for /admin/finance/pnl/month/{month}.
        </div>
      )}

      {monthQ.isLoading ? (
        <div className="p-6 text-sm text-zinc-500">Loading {formatMonthLabel(month)}…</div>
      ) : !detail ? null : (
        <MonthDetailSections detail={detail} />
      )}
    </div>
  );
}

export default PnlMonthDetailPage;
